import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { ConnectionStatus } from '../types/protocol';

interface ConnectFormProps {
  connectionStatus: ConnectionStatus;
  wsStatus: string;
  clusters: Array<{ id: string; label: string }>;
  defaultServerUrl?: string;
  onConnect: (serverUrl: string) => void;
  onSetContext: (cluster: string, userId: number) => void;
}

export function ConnectForm({
  connectionStatus,
  wsStatus,
  clusters,
  defaultServerUrl,
  onConnect,
  onSetContext,
}: ConnectFormProps): React.ReactElement {
  const [serverUrl, setServerUrl] = useState(defaultServerUrl ?? 'ws://localhost:8082/ws');
  const [cluster, setCluster] = useState(clusters[0]?.id ?? 'qa');
  const [userId, setUserId] = useState('1');

  useEffect(() => {
    const first = clusters[0]?.id;
    if (first) setCluster(first);
  }, [clusters]);

  const handleConnect = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      onConnect(serverUrl);
    },
    [serverUrl, onConnect],
  );

  const handleSetContext = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      onSetContext(cluster, Number(userId) || 1);
    },
    [cluster, userId, onSetContext],
  );

  const { t } = useTranslation();
  const isWsConnected = wsStatus === 'connected';
  const isContextSet = connectionStatus === 'context_set';

  return (
    <div className="sp-connect-form">
      <div className="sp-connect-header">
        <h2 className="sp-connect-title">{t('app.name')}</h2>
        <p className="sp-connect-subtitle">{t('app.subtitle')}</p>
      </div>

      <form className="sp-connect-section" onSubmit={handleConnect}>
        <label className="sp-connect-label">
          {t('connect.serverUrl')}
          <input
            className="sp-connect-input"
            type="text"
            value={serverUrl}
            onChange={(e) => setServerUrl(e.target.value)}
            disabled={isWsConnected}
          />
        </label>
        <div className="sp-connect-row">
          <button
            className="sp-btn sp-btn-primary"
            type="submit"
            disabled={isWsConnected || wsStatus === 'connecting'}
          >
            {wsStatus === 'connecting' ? t('connect.connecting') : isWsConnected ? t('connect.connected') : t('connect.connect')}
          </button>
          <span className={`sp-status-badge sp-status-${wsStatus}`}>{wsStatus}</span>
        </div>
      </form>

      {isWsConnected && !isContextSet && (
        <form className="sp-connect-section" onSubmit={handleSetContext}>
          <label className="sp-connect-label">
            {t('connect.cluster')}
            <select
              className="sp-connect-input"
              value={cluster}
              onChange={(e) => setCluster(e.target.value)}
            >
              {clusters.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.label}
                </option>
              ))}
            </select>
          </label>
          <label className="sp-connect-label">
            {t('connect.userId')}
            <input
              className="sp-connect-input"
              type="number"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              min="1"
            />
          </label>
          <button className="sp-btn sp-btn-primary" type="submit">
            {t('connect.setContext')}
          </button>
        </form>
      )}
    </div>
  );
}
